import React, { useState } from 'react';
import { Asset } from '../types';
import { v4 as uuidv4 } from 'uuid';

interface AssetFormProps {
  onAddAsset: (asset: Asset) => void;
}

const AssetForm: React.FC<AssetFormProps> = ({ onAddAsset }) => {
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [volatility, setVolatility] = useState('');
  const [annualContribution, setAnnualContribution] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const newAsset: Asset = {
      id: uuidv4(),
      name,
      amount: parseFloat(amount),
      interestRate: parseFloat(interestRate),
      volatility: parseFloat(volatility),
      annualContribution: parseFloat(annualContribution) || 0
    };
    
    onAddAsset(newAsset);
    
    // Reset form
    setName('');
    setAmount('');
    setInterestRate('');
    setVolatility('');
    setAnnualContribution('');
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-bold mb-4">Add New Asset</h2>
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Asset Name
          </label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        
        <div className="mb-4">
          <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
            Current Amount ($)
          </label>
          <input
            type="number"
            id="amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            min="0"
            step="1000"
            required
          />
        </div>
        
        <div className="mb-4">
          <label htmlFor="interestRate" className="block text-sm font-medium text-gray-700 mb-1">
            Annual Interest Rate (%)
          </label>
          <input
            type="number"
            id="interestRate"
            value={interestRate}
            onChange={(e) => setInterestRate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            min="0"
            max="100"
            step="0.1"
            required
          />
        </div>
        
        <div className="mb-4">
          <label htmlFor="volatility" className="block text-sm font-medium text-gray-700 mb-1">
            Annual Volatility (%)
          </label>
          <input
            type="number"
            id="volatility"
            value={volatility}
            onChange={(e) => setVolatility(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            min="0"
            max="100"
            step="0.1"
            required
          />
        </div>
        
        <div className="mb-4">
          <label htmlFor="annualContribution" className="block text-sm font-medium text-gray-700 mb-1">
            Annual Contribution ($)
          </label>
          <input
            type="number"
            id="annualContribution"
            value={annualContribution}
            onChange={(e) => setAnnualContribution(e.target.value)}
            placeholder="Amount to add yearly until retirement"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            min="0"
            step="100"
          />
          <p className="text-xs text-gray-500 mt-1">
            Amount you plan to contribute annually to this asset until retirement
          </p>
        </div>
        
        <button
          type="submit"
          className="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 transition-colors"
        >
          Add Asset
        </button>
      </form>
    </div>
  );
};

export default AssetForm;